import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Etiqueta, type MensajeProveedor, type MensajesProveedores, type Proveedor, type ResultadoBaja } from "@/lib/api";
import { linkWhatsapp } from "@/lib/telefonos";
import { armarMensaje, conTexto, mensajesDelProveedor, valoresMensaje } from "@/lib/mensajes";
import {
  agregarEtiqueta,
  aPayloadProveedor,
  filtrarProveedores,
  formProveedor,
  mensajeBaja,
  opcionesEtiquetaForm,
  opcionesFiltroEtiqueta,
  quitarEtiqueta,
  TODAS_LAS_ETIQUETAS,
  validarProveedor,
  type CampoTextoProveedor,
  type ErroresProveedor,
  type FormProveedor,
  type PayloadProveedor,
} from "@/lib/proveedores";

type Dialogo =
  | { tipo: "form"; proveedor: Proveedor | null }
  | { tipo: "ver"; proveedor: Proveedor }
  | { tipo: "borrar"; proveedor: Proveedor }
  | { tipo: "mensaje"; proveedor: Proveedor; opciones: MensajeProveedor[] }
  | null;

const SIN_ERRORES: ErroresProveedor = {};

/** Lógica de Proveedores (sin JSX). La agenda es compartida entre desarrollos: el admin la ve y edita entera; el contador
 * ve la ficha de los vinculados a este desarrollo; el fiduciante, sólo nombre y etiquetas (el backend filtra). */
export function useProveedoresTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/proveedores`;
  // Se piden todos (archivados incluidos) y se filtra acá: el tilde "Ver archivados" no vuelve a pedir.
  const lista = useApiResource<Proveedor[]>(`${base}?archivados=true`);
  const etiquetas = useApiResource<Etiqueta[]>(`/api/fideicomisos/${fid}/etiquetas`);
  const esAdmin = membresia.rol === "admin";

  const [texto, setTexto] = useState("");
  const [etiquetaFiltro, setEtiquetaFiltro] = useState(TODAS_LAS_ETIQUETAS);
  const [archivados, setArchivados] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormProveedor>(() => formProveedor());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [errorEtiqueta, setErrorEtiqueta] = useState<string | null>(null);
  const catalogo = etiquetas.data ?? [];
  // Configuración → Mensajes a proveedores. Si no carga, el WhatsApp abre el chat vacío, como antes.
  const mensajes = useApiResource<MensajesProveedores>(`/api/fideicomisos/${fid}/mensajes-proveedores`);
  const [mensajeId, setMensajeId] = useState<number | null>(null);
  const [textoMensaje, setTextoMensaje] = useState("");
  const [copiado, setCopiado] = useState(false);

  const accionGuardar = useAsyncAction((id: number | null, datos: PayloadProveedor) =>
    id === null ? api.post<Proveedor>(base, datos) : api.patch<Proveedor>(`${base}/${id}`, datos),
  );
  const accionEstado = useAsyncAction((id: number, activo: boolean) => api.patch<Proveedor>(`${base}/${id}`, { activo }));
  const accionBorrar = useAsyncAction((id: number) => api.delete<ResultadoBaja>(`${base}/${id}`));

  const errores = validarProveedor(form);
  const cerrar = () => setDialogo(null);

  const abrirForm = (proveedor: Proveedor | null) => {
    setForm(formProveedor(proveedor ?? undefined));
    setMostrarErrores(false);
    setErrorEtiqueta(null);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", proveedor });
  };

  const cambiar = (campo: CampoTextoProveedor, valor: string) => setForm((f) => ({ ...f, [campo]: valor }));
  const cambiarValidado = (validado: boolean) => setForm((f) => ({ ...f, validado }));

  const agregar = (texto: string) => {
    const r = agregarEtiqueta(form.etiquetas, texto, catalogo.map((e) => e.nombre));
    setErrorEtiqueta(r.error);
    if (r.etiquetas !== form.etiquetas) setForm((f) => ({ ...f, etiquetas: r.etiquetas }));
  };

  const quitar = (nombre: string) => {
    setErrorEtiqueta(null);
    setForm((f) => ({ ...f, etiquetas: quitarEtiqueta(f.etiquetas, nombre) }));
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.proveedor?.id ?? null, aPayloadProveedor(form))) {
      cerrar();
      lista.recargar();
      etiquetas.recargar(); // pudo haber etiquetas nuevas
    }
  };

  const cambiarEstado = async (p: Proveedor) => {
    setAviso(null);
    if (await accionEstado.run(p.id, !p.activo)) lista.recargar();
  };

  const abrirBorrar = (p: Proveedor) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", proveedor: p });
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    const r = await accionBorrar.run(dialogo.proveedor.id);
    if (!r) return;
    setAviso(mensajeBaja(r.resultado, dialogo.proveedor.nombre));
    cerrar();
    lista.recargar();
  };

  const etiquetaElegida = catalogo.find((e) => String(e.id) === etiquetaFiltro)?.nombre ?? null;

  const armar = (p: Proveedor, m: MensajeProveedor) => (mensajes.data ? armarMensaje(m.texto, valoresMensaje(mensajes.data, p.nombre)) : null);

  const elegirMensaje = (p: Proveedor, m: MensajeProveedor) => {
    setMensajeId(m.id);
    setTextoMensaje(armar(p, m)?.texto ?? m.texto);
    setCopiado(false);
  };

  const abrirMensaje = (p: Proveedor) => {
    const opciones = mensajesDelProveedor(mensajes.data ?? null, p.etiquetas);
    if (opciones.length === 0) return;
    elegirMensaje(p, opciones[0]!);
    setDialogo({ tipo: "mensaje", proveedor: p, opciones });
  };

  const dialogoMensaje = dialogo?.tipo === "mensaje" ? dialogo : null;
  const elegido = dialogoMensaje?.opciones.find((m) => m.id === mensajeId) ?? null;
  const linkChat = dialogoMensaje ? linkWhatsapp(dialogoMensaje.proveedor.telefono) : null;

  const copiarMensaje = async () => {
    try {
      await navigator.clipboard.writeText(textoMensaje);
      setCopiado(true);
    } catch {
      setCopiado(false); // sin permiso de portapapeles: se copia a mano desde el cuadro
    }
  };

  return {
    tieneMensaje: (p: Proveedor) => mensajesDelProveedor(mensajes.data ?? null, p.etiquetas).length > 0,
    abrirMensaje,
    opcionesMensaje: (dialogoMensaje?.opciones ?? []).map((m) => ({ valor: String(m.id), etiqueta: m.etiqueta ?? "General" })),
    mensajeElegido: mensajeId === null ? undefined : String(mensajeId),
    elegirMensaje: (id: string) => {
      const m = dialogoMensaje?.opciones.find((o) => String(o.id) === id);
      if (dialogoMensaje && m) elegirMensaje(dialogoMensaje.proveedor, m);
    },
    textoMensaje,
    setTextoMensaje,
    avisosMensaje: dialogoMensaje && elegido ? armar(dialogoMensaje.proveedor, elegido) : null,
    linkMensaje: linkChat && textoMensaje.trim() ? conTexto(linkChat, textoMensaje.trim()) : null,
    copiarMensaje,
    copiado,
    proveedores: filtrarProveedores(lista.data ?? [], { texto, etiqueta: etiquetaElegida, archivados }),
    cargando: lista.loading && !lista.data,
    errorCarga: lista.data ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    esAdmin,
    texto,
    setTexto,
    etiquetaFiltro,
    setEtiquetaFiltro,
    /** Tocar una etiqueta de la lista: filtra por ella. */
    filtrarPorEtiqueta: (nombre: string) => {
      const e = catalogo.find((x) => x.nombre === nombre);
      if (e) setEtiquetaFiltro(String(e.id));
    },
    opcionesFiltro: opcionesFiltroEtiqueta(catalogo),
    errorEtiquetas: etiquetas.data ? null : (etiquetas.error?.message ?? null),
    reintentarEtiquetas: etiquetas.recargar,
    archivados,
    alternarArchivados: () => setArchivados((a) => !a),
    aviso,
    dialogo,
    cerrar,
    form,
    cambiar,
    cambiarValidado,
    errores: mostrarErrores ? errores : SIN_ERRORES,
    opcionesEtiquetaForm: opcionesEtiquetaForm(catalogo, form.etiquetas),
    agregarEtiqueta: agregar,
    quitarEtiqueta: quitar,
    errorEtiqueta: errorEtiqueta ?? (mostrarErrores ? (errores.etiquetas ?? null) : null),
    abrirNuevo: () => abrirForm(null),
    abrirEditar: (p: Proveedor) => abrirForm(p),
    abrirVer: (p: Proveedor) => setDialogo({ tipo: "ver", proveedor: p }),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    cambiarEstado,
    errorEstado: accionEstado.error,
    abrirBorrar,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
